import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { useLanguage } from "@/i18n/LanguageContext";
import { Type } from "lucide-react";

export function HeaderSettingsCard({
  headerEnabled,
  setHeaderEnabled,
  headerText,
  setHeaderText,
  headerFontSize,
  setHeaderFontSize,
  headerTextColor,
  setHeaderTextColor,
  headerBgColor,
  setHeaderBgColor,
  headerBorderRadius,
  setHeaderBorderRadius,
  headerPadding,
  setHeaderPadding
}) {
  const { t } = useLanguage();

  return (
    <Card className="col-span-12 bento-card" data-testid="header-settings-card">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
            <Type className="w-5 h-5" />
            {t("headerBox")}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Label htmlFor="header-enabled" className={headerEnabled ? "text-green-400" : "text-zinc-500"}>
              {headerEnabled ? t("on") : t("off")}
            </Label>
            <Switch
              id="header-enabled"
              checked={headerEnabled}
              onCheckedChange={setHeaderEnabled}
              data-testid="header-enabled-switch"
              aria-label={t("headerBox")}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className={`space-y-4 ${!headerEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="space-y-2">
          <Label className="text-zinc-400">{t("headerText")}</Label>
          <Input
            value={headerText}
            onChange={(e) => setHeaderText(e.target.value)}
            className="bg-zinc-900 border-zinc-700 text-white"
            placeholder={t("headerText")}
            data-testid="header-text-input"
          />
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">{t("fontSize")}</Label>
            <div className="flex items-center gap-2">
              <Slider
                value={[headerFontSize]}
                onValueChange={(value) => setHeaderFontSize(value[0])}
                min={12}
                max={48}
                step={1}
                className="flex-1"
                data-testid="header-font-size-slider"
              />
              <span className="text-white font-mono text-sm w-10">{headerFontSize}px</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">{t("padding")}</Label>
            <div className="flex items-center gap-2">
              <Slider
                value={[headerPadding]}
                onValueChange={(value) => setHeaderPadding(value[0])}
                min={4}
                max={32}
                step={2}
                className="flex-1"
                data-testid="header-padding-slider"
              />
              <span className="text-white font-mono text-sm w-10">{headerPadding}px</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">{t("roundCorners")}</Label>
            <div className="flex items-center gap-2">
              <Slider
                value={[headerBorderRadius]}
                onValueChange={(value) => setHeaderBorderRadius(value[0])}
                min={0}
                max={24}
                step={2}
                className="flex-1"
                data-testid="header-border-radius-slider"
              />
              <span className="text-white font-mono text-sm w-10">{headerBorderRadius}px</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">{t("colors")}</Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label className="text-zinc-500 text-xs">{t("text")}</Label>
                <input
                  type="color"
                  value={headerTextColor}
                  onChange={(e) => setHeaderTextColor(e.target.value)}
                  className="w-full h-8 rounded cursor-pointer"
                  data-testid="header-text-color"
                />
              </div>
              <div className="flex-1">
                <Label className="text-zinc-500 text-xs">{t("background")}</Label>
                <input
                  type="color"
                  value={headerBgColor}
                  onChange={(e) => setHeaderBgColor(e.target.value)}
                  className="w-full h-8 rounded cursor-pointer"
                  data-testid="header-bg-color"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Preview */}
        <div className="mt-4 p-4 bg-zinc-900/50 rounded-lg">
          <Label className="text-zinc-400 text-sm mb-2 block">{t("preview")}:</Label>
          <div className="flex justify-center">
            <div
              style={{
                backgroundColor: headerBgColor,
                color: headerTextColor,
                fontSize: `${headerFontSize}px`,
                padding: `${headerPadding}px`,
                borderRadius: `${headerBorderRadius}px`,
              }}
            >
              {headerText || t("headerText")}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function FooterSettingsCard({
  footerEnabled,
  setFooterEnabled,
  footerText,
  setFooterText,
  footerFontSize,
  setFooterFontSize,
  footerTextColor,
  setFooterTextColor,
  footerBgColor,
  setFooterBgColor,
  footerBorderRadius,
  setFooterBorderRadius,
  footerPadding,
  setFooterPadding
}) {
  const { t } = useLanguage();

  return (
    <Card className="col-span-12 bento-card" data-testid="footer-settings-card">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
            <Type className="w-5 h-5" />
            {t("footerBox")}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Label htmlFor="footer-enabled" className={footerEnabled ? "text-green-400" : "text-zinc-500"}>
              {footerEnabled ? t("on") : t("off")}
            </Label>
            <Switch
              id="footer-enabled"
              checked={footerEnabled}
              onCheckedChange={setFooterEnabled}
              data-testid="footer-enabled-switch"
              aria-label={t("footerBox")}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className={`space-y-4 ${!footerEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="space-y-2">
          <Label className="text-zinc-400">{t("footerText")}</Label>
          <Textarea
            value={footerText}
            onChange={(e) => setFooterText(e.target.value)}
            className="bg-zinc-900 border-zinc-700 text-white min-h-[80px]"
            placeholder={t("footerText")}
            data-testid="footer-text-input"
          />
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">{t("fontSize")}</Label>
            <div className="flex items-center gap-2">
              <Slider
                value={[footerFontSize]}
                onValueChange={(value) => setFooterFontSize(value[0])}
                min={10}
                max={32}
                step={1}
                className="flex-1"
                data-testid="footer-font-size-slider"
              />
              <span className="text-white font-mono text-sm w-10">{footerFontSize}px</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">{t("padding")}</Label>
            <div className="flex items-center gap-2">
              <Slider
                value={[footerPadding]}
                onValueChange={(value) => setFooterPadding(value[0])}
                min={4}
                max={32}
                step={2}
                className="flex-1"
                data-testid="footer-padding-slider"
              />
              <span className="text-white font-mono text-sm w-10">{footerPadding}px</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">{t("roundCorners")}</Label>
            <div className="flex items-center gap-2">
              <Slider
                value={[footerBorderRadius]}
                onValueChange={(value) => setFooterBorderRadius(value[0])}
                min={0}
                max={24}
                step={2}
                className="flex-1"
                data-testid="footer-border-radius-slider"
              />
              <span className="text-white font-mono text-sm w-10">{footerBorderRadius}px</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">{t("colors")}</Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label className="text-zinc-500 text-xs">{t("text")}</Label>
                <input
                  type="color"
                  value={footerTextColor}
                  onChange={(e) => setFooterTextColor(e.target.value)}
                  className="w-full h-8 rounded cursor-pointer"
                  data-testid="footer-text-color"
                />
              </div>
              <div className="flex-1">
                <Label className="text-zinc-500 text-xs">{t("background")}</Label>
                <input
                  type="color"
                  value={footerBgColor}
                  onChange={(e) => setFooterBgColor(e.target.value)}
                  className="w-full h-8 rounded cursor-pointer"
                  data-testid="footer-bg-color"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Preview */}
        <div className="mt-4 p-4 bg-zinc-900/50 rounded-lg">
          <Label className="text-zinc-400 text-sm mb-2 block">{t("preview")}:</Label>
          <div className="flex justify-center">
            <div
              style={{
                backgroundColor: footerBgColor,
                color: footerTextColor,
                fontSize: `${footerFontSize}px`,
                padding: `${footerPadding}px`,
                borderRadius: `${footerBorderRadius}px`,
                maxWidth: '80%',
                textAlign: 'center',
              }}
            >
              {footerText || t("footerText")}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
